package app.nestbridge.feature.invitations.api

import app.nestbridge.app.di.ClientApiConfig
import app.nestbridge.feature.invitations.api.model.InvitationDto
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.serialization.Serializable
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import software.amazon.lastmile.kotlin.inject.anvil.ContributesBinding
import software.amazon.lastmile.kotlin.inject.anvil.SingleIn

@Serializable
private data class AcceptInvitationRequest(val verificationCode: String? = null)

@SingleIn(AppScope::class)
@ContributesBinding(AppScope::class)
@Inject
class RemoteInvitationApiClient(
    private val httpClient: HttpClient,
    private val config: ClientApiConfig,
) : InvitationApiClient {

    private val baseUrl get() = "${config.childServiceUrl}/invitations"

    override suspend fun fetchSentInvitations(): List<InvitationDto> =
        httpClient.get("$baseUrl/sent").body()

    override suspend fun fetchReceivedInvitations(): List<InvitationDto> =
        httpClient.get("$baseUrl/received").body()

    override suspend fun fetchById(invitationId: String): InvitationDto? {
        val response: HttpResponse = httpClient.get("$baseUrl/$invitationId")
        return when (response.status) {
            HttpStatusCode.OK -> response.body()
            HttpStatusCode.NotFound -> null
            else -> error("Unexpected response fetching invitation '$invitationId': ${response.status}")
        }
    }

    override suspend fun fetchByClaimToken(token: String): InvitationDto? {
        val response: HttpResponse = httpClient.get("$baseUrl/claim/$token")
        return when (response.status) {
            HttpStatusCode.OK -> response.body()
            HttpStatusCode.NotFound -> null
            else -> error("Unexpected response fetching invitation by claim token: ${response.status}")
        }
    }

    override suspend fun fetchPendingTeamCreationInvitation(childId: String): InvitationDto? {
        val response: HttpResponse = httpClient.get("$baseUrl/team-creation/child/$childId")
        return when (response.status) {
            HttpStatusCode.OK -> response.body()
            HttpStatusCode.NotFound -> null
            else -> error("Unexpected response fetching pending team creation invitation for child '$childId': ${response.status}")
        }
    }

    override suspend fun create(invitation: InvitationDto): InvitationDto =
        httpClient.post(baseUrl) {
            contentType(ContentType.Application.Json)
            setBody(invitation)
        }.body()

    override suspend fun initiateVerification(invitationId: String) {
        httpClient.post("$baseUrl/$invitationId/initiate-verification")
    }

    override suspend fun accept(invitationId: String) {
        httpClient.post("$baseUrl/$invitationId/accept") {
            contentType(ContentType.Application.Json)
            setBody(AcceptInvitationRequest())
        }
    }

    override suspend fun accept(invitationId: String, verificationCode: String): Result<Unit> =
        runCatching {
            httpClient.post("$baseUrl/$invitationId/accept") {
                contentType(ContentType.Application.Json)
                setBody(AcceptInvitationRequest(verificationCode = verificationCode))
            }
        }

    override suspend fun decline(invitationId: String) {
        httpClient.post("$baseUrl/$invitationId/decline")
    }

    override suspend fun rescind(invitationId: String) {
        httpClient.post("$baseUrl/$invitationId/rescind")
    }
}
